/** Display helpers for Log Explorer rows. Formatting never changes the stored payload. */

export function compactMessage(message, max = 160) {
  const one = String(message || "").replace(/\s+/g, " ").trim();
  if (one.length <= max) return one;
  return `${one.slice(0, max - 1)}…`;
}

export function formatPayload(message) {
  const text = String(message ?? "");
  const trimmed = text.trim();
  if (!trimmed) return { kind: "empty", text: "" };
  if (trimmed.startsWith("{") || trimmed.startsWith("[")) {
    try {
      return { kind: "json", text: JSON.stringify(JSON.parse(trimmed), null, 2) };
    } catch {
      return { kind: "text", text };
    }
  }
  if (trimmed.startsWith("<")) {
    const formatted = formatXml(trimmed);
    if (formatted) return { kind: "xml", text: formatted };
    return { kind: "text", text };
  }
  return { kind: "text", text };
}

export function eventDetailRows(log) {
  const meta = log?.normalizedMetadata && typeof log.normalizedMetadata === "object"
    ? log.normalizedMetadata
    : {};
  const rows = [];
  const add = (label, value) => {
    if (value == null || value === "") return;
    rows.push({
      label,
      value: typeof value === "string" ? value : JSON.stringify(value, null, 2)
    });
  };
  add("Timestamp", log?.timestamp);
  add("Level", log?.level);
  add("Service", log?.service);
  add("Index", log?.index || log?.indexName || meta.index);
  add("Application", log?.application || meta.application);
  add("Environment", meta.environment);
  add("Platform", meta.platform);
  add("Trace ID", log?.traceId);
  add("User ID", log?.userId);
  add("IP address", log?.ip);
  add("User agent", log?.userAgent);
  add("Created", log?.createdAt);
  add("Host", meta.host);
  add("Container", meta.container);
  add("Pod", meta.pod);
  add("Span ID", meta.spanId);
  const known = new Set([
    "environment", "platform", "application", "index", "host", "container", "pod", "spanId", "source", "tags"
  ]);
  Object.keys(meta).forEach((key) => {
    if (!known.has(key)) add(key, meta[key]);
  });
  if (meta.tags && typeof meta.tags === "object") add("Tags", meta.tags);
  return rows;
}

function formatXml(xml) {
  if (typeof DOMParser === "undefined") return null;
  const doc = new DOMParser().parseFromString(xml, "application/xml");
  if (doc.querySelector("parsererror")) return null;
  if (!doc.documentElement) return null;
  return prettyNode(doc.documentElement, 0).trim();
}

function prettyNode(node, depth) {
  const pad = "  ".repeat(depth);
  if (node.nodeType === 3) {
    const text = node.textContent.replace(/\s+/g, " ").trim();
    return text ? `${pad}${text}\n` : "";
  }
  if (node.nodeType !== 1) return "";
  const name = node.nodeName;
  const children = [...node.childNodes].filter((child) => !(child.nodeType === 3 && !child.textContent.trim()));
  if (children.length === 0) return `${pad}<${name}></${name}>\n`;
  if (children.length === 1 && children[0].nodeType === 3) {
    return `${pad}<${name}>${children[0].textContent.trim()}</${name}>\n`;
  }
  return `${pad}<${name}>\n${children.map((child) => prettyNode(child, depth + 1)).join("")}${pad}</${name}>\n`;
}
